import { Toast as Radix } from "radix-ui";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { cx } from "../../lib/cx";
import styles from "./Toast.module.css";

export type ToastTone = "neutral" | "success" | "danger";

export interface ToastInput {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** ms; default 5000. */
  duration?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

const ToastContext = createContext<((toast: ToastInput) => void) | null>(null);

let nextId = 1;

/** Mount once at the app root; `useToast()` then shows notifications. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const show = useCallback((toast: ToastInput) => {
    setItems((current) => [...current.slice(-2), { ...toast, id: nextId++ }]);
  }, []);
  const remove = (id: number) => setItems((current) => current.filter((t) => t.id !== id));
  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      <Radix.Provider swipeDirection="right">
        {children}
        {items.map((item) => (
          <Radix.Root
            key={item.id}
            duration={item.duration ?? 5000}
            className={cx(styles.toast, item.tone && styles[item.tone])}
            onOpenChange={(open) => {
              if (!open) remove(item.id);
            }}
          >
            <Radix.Title className={styles.title}>{item.title}</Radix.Title>
            {item.description ? (
              <Radix.Description className={styles.description}>
                {item.description}
              </Radix.Description>
            ) : null}
            <Radix.Close className={styles.close} aria-label="Dismiss">
              <svg
                viewBox="0 0 16 16"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </Radix.Close>
          </Radix.Root>
        ))}
        <Radix.Viewport className={styles.viewport} />
      </Radix.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): (toast: ToastInput) => void {
  const show = useContext(ToastContext);
  if (!show) throw new Error("useToast must be used inside <ToastProvider>");
  return show;
}
